import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Send, CheckCircle2 } from "lucide-react";
import { streamSSE } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

function renderText(t) {
  return t.split(/(\/(?:projects|contact|services|process|about|reviews)[\w/-]*)/g).map((part, i) =>
    /^\/(projects|contact|services|process|about|reviews)/.test(part) ? <Link key={i} to={part} className="font-semibold text-[color:var(--ib-orange)] underline">{part}</Link> : part
  );
}

function Bubble({ m }) {
  const mine = m.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] whitespace-pre-wrap px-3.5 py-2.5 text-sm leading-relaxed ${mine ? "bg-[color:var(--ib-charcoal)] text-white" : "bg-[color:var(--ib-off)] text-[color:var(--ib-charcoal)]"}`}>
        {renderText(m.text)}{m.streaming && <span className="ml-1 inline-block h-3 w-1.5 animate-pulse bg-current align-middle" />}
      </div>
    </div>
  );
}

export default function ChatWidget() {
  const { site } = useSite();
  const loc = useLocation();
  const ai = site?.ai || {};
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState(() => JSON.parse(sessionStorage.getItem("ib_chat") || "[]"));
  const [cid, setCid] = useState(() => sessionStorage.getItem("ib_cid") || null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { sessionStorage.setItem("ib_chat", JSON.stringify(msgs.filter((m) => !m.streaming))); endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  useEffect(() => { if (cid) sessionStorage.setItem("ib_cid", cid); }, [cid]);
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("ib-open-chat", onOpen);
    return () => window.removeEventListener("ib-open-chat", onOpen);
  }, []);

  if (!site || ai.enabled === false || (ai.hidden_paths || []).some((p) => p && loc.pathname.startsWith(p))) return null;

  const send = async (text) => {
    const t = (text || input).trim();
    if (!t || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: t }, { role: "assistant", text: "", streaming: true }]);
    const patch = (fn) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = fn(c[c.length - 1]); return c; });
    try {
      await streamSSE("/public/chat", { message: t, conversation_id: cid, page: loc.pathname }, (ev) => {
        if (ev.type === "meta") setCid(ev.conversation_id);
        if (ev.type === "delta") patch((l) => ({ ...l, text: l.text + ev.text }));
        if (ev.type === "done") {
          patch((l) => ({ ...l, streaming: false }));
          if (ev.lead_created) setLeadDone(true);
        }
      });
    } catch (e) {
      patch(() => ({ role: "assistant", text: e.message || "Sorry, something went wrong. Please call us." }));
    }
    setBusy(false);
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} className="fixed bottom-5 right-5 z-50 flex items-center gap-3 rounded-full bg-[color:var(--ib-orange)] py-1.5 pl-1.5 pr-5 text-white shadow-2xl transition-transform hover:scale-105" data-testid="chat-launcher" aria-label="Ask Jason — open the Ironbark AI assistant chat">
          <span className="relative">
            <img src="/media/jason-avatar.jpg" alt="" className="h-11 w-11 rounded-full border-2 border-white object-cover" />
            <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-[color:var(--ib-orange)] bg-green-400" aria-hidden="true" />
          </span>
          <span className="font-display text-sm uppercase tracking-wider">Ask Jason</span>
        </button>
      )}
      {open && (
        <div className="fixed inset-x-0 bottom-0 z-50 flex h-[85vh] flex-col bg-white shadow-2xl sm:inset-x-auto sm:bottom-5 sm:right-5 sm:h-[620px] sm:w-[400px] sm:border sm:border-black/10" role="dialog" aria-label={ai.name} data-testid="chat-panel">
          <div className="flex items-center justify-between bg-[color:var(--ib-charcoal)] px-4 py-3 text-white">
            <div className="flex items-center gap-3">
              <img src="/media/jason-avatar.jpg" alt="Jason Price" className="h-10 w-10 rounded-full border-2 border-white/80 object-cover" />
              <div><p className="font-display text-sm uppercase tracking-wider">Ask Jason</p><p className="text-[0.7rem] text-white/60">Jason's AI assistant · answers may need confirmation</p></div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close chat" data-testid="chat-close"><X size={20} /></button>
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="chat-messages">
            <Bubble m={{ role: "assistant", text: ai.welcome }} />
            {msgs.length === 0 && (
              <div className="flex flex-wrap gap-2 pt-1" data-testid="chat-starters">
                {(ai.starters || []).map((s, i) => (
                  <button key={s} onClick={() => send(s)} className="rounded-md border border-[color:var(--ib-orange)] px-3 py-1.5 text-xs font-semibold text-[color:var(--ib-orange)] transition-colors hover:bg-[color:var(--ib-orange)] hover:text-white" data-testid={`chat-starter-${i}`}>{s}</button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => <Bubble key={i} m={m} />)}
            {leadDone && <p className="flex items-center gap-2 bg-green-50 p-3 text-xs text-green-800" data-testid="chat-lead-confirmation"><CheckCircle2 size={16} /> Your project details were sent to Ironbark Construction. The team will reach out to you.</p>}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 border-t border-black/10 p-3">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type your question…" aria-label="Message" className="flex-1 rounded-md border border-black/15 px-3 py-2 text-sm outline-none focus:border-[color:var(--ib-orange)]" data-testid="chat-input" />
            <button disabled={busy} className="rounded-md bg-[color:var(--ib-orange)] px-3 text-white disabled:opacity-50" aria-label="Send" data-testid="chat-send-btn"><Send size={18} /></button>
          </form>
        </div>
      )}
    </>
  );
}
